import { PickupPoint } from '../../models';
import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';
import { ShippingService } from './shipping-service';

// ─── Sendcloud response shape (used by legacy endpoint) ──────────────────────

interface SendcloudPickupPoint {
  id: number;
  code: string;
  is_active: boolean;
  shop_type: string;
  extra_data: { shop_type: string };
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  latitude: string;
  longitude: string;
  email: string;
  phone: string;
  homepage: string;
  carrier: string;
  country: string;
  formatted_opening_times: { [key: string]: string[] };
  open_tomorrow: boolean;
  open_upcoming_week: boolean;
  general_shop_type: string;
}

const formatOpeningHours = (times: { [key: string]: string[] }): string => {
  const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  return days
    .map((day, i) => {
      const hours = times[i.toString()];
      return hours?.length ? `${day}: ${hours.join(', ')}` : `${day}: Fermé`;
    })
    .join(' | ');
};

const convertSendcloudToPickupPoint = (p: SendcloudPickupPoint): PickupPoint => ({
  id: p.id.toString(),
  /** Requis pour Mondial Relay Connect (création d’étiquette) — même format que l’API Sendcloud */
  code: typeof p.code === 'string' && p.code.trim() ? p.code.trim() : undefined,
  name: p.name,
  address: `${p.house_number} ${p.street}`.trim(),
  city: p.city,
  postalCode: p.postal_code,
  country: p.country,
  latitude: Number.parseFloat(p.latitude),
  longitude: Number.parseFloat(p.longitude),
  openingHours: formatOpeningHours(p.formatted_opening_times),
  provider: p.carrier?.toLowerCase().includes('mondial') ? 'mondial_relay' : 'chronopost',
});

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 100) / 100;
};

export class PickupService {
  /**
   * Fetch relay/pickup points via the multi-provider Cloud Function.
   * Routing (Sendcloud vs Mondial Relay Connect) is handled server-side based on the method config.
   */
  static async fetchPickupPoints(
    postalCode: string,
    orgId: string,
    shippingMethodId: string,
    country: string = 'FR'
  ): Promise<PickupPoint[]> {
    return ShippingService.getRelayPoints(orgId, shippingMethodId, country, postalCode);
  }

  /**
   * Legacy Sendcloud-only fetch (used when orgId / shippingMethodId are unavailable).
   * Calls the old getSendCloudServicePoints CF directly.
   */
  static async fetchPickupPointsLegacy(
    postalCode: string,
    carrier: string = 'chronopost',
    country: string = 'FR'
  ): Promise<PickupPoint[]> {
    const params = new URLSearchParams({ carrier, country, postal_code: postalCode });
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getSendCloudServicePoints?${params}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data: SendcloudPickupPoint[] = await response.json();
    return data
      .filter(p => p.is_active)
      .map(convertSendcloudToPickupPoint)
      .slice(0, 20);
  }

  static async fetchNearbyPickupPoints(
    postalCode: string,
    orgId: string,
    shippingMethodId: string,
    country: string = 'FR',
    userLat?: number,
    userLon?: number
  ): Promise<PickupPoint[]> {
    const points = await this.fetchPickupPoints(postalCode, orgId, shippingMethodId, country);
    if (userLat && userLon) {
      return points
        .map(p => ({ ...p, distance: calculateDistance(userLat, userLon, p.latitude, p.longitude) }))
        .sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
    }
    return points;
  }
}

// Legacy export kept for backward compat
export const fetchPickupPointsFromSendcloud = PickupService.fetchPickupPointsLegacy.bind(PickupService);
export const fetchNearbyPickupPoints = PickupService.fetchNearbyPickupPoints.bind(PickupService);
